import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SafeUrl } from '@angular/platform-browser';
import { combineLatest, map, Observable, shareReplay } from 'rxjs';
import * as R from 'runtypes';

import {
  ApiTransaction,
  ApiTransactionEnd,
  ApiVault,
  Currency,
  ImageService,
  ApiTransactionsResponse,
  MarketService,
  TransactionStatus,
  ChilledWallet,
  defined,
  TransactionStatusSummary,
  Scrollable,
} from 'core';
import { URLS } from 'commons/app.constant';
import BigNumber from 'bignumber.js';
import { SendAddress, SendAddressType } from 'entities/send.address';

export type Vault = {
  assets: Array<ChilledWallet>;
};
const ApiChilledAsset = R.Record({
  network: R.String,
  currencyCd: R.String,
});
export type ApiChilledAsset = R.Static<typeof ApiChilledAsset>;

export type ChilledAsset = ApiChilledAsset & {
  currency: Currency;
};

const ApiWalletAddress = R.Record({
  address: R.String,
  addressQRCode: R.String,

  tag: R.String.optional(),
  tagQRCode: R.String.optional(),
});
type ApiWalletAddress = R.Static<typeof ApiWalletAddress>;

export type WalletAddress = {
  address: string;
  addressQRCode: SafeUrl;

  tag?: string;
  tagQRCode?: SafeUrl;
};

export interface ChilledTransaction extends Scrollable<string> {
  currency: Currency;
  network: string | null;
  feeCurrency: Currency;
  amount: number;
  amountUsd: number | null;
  fee: number;
  feeUsd: number | null;
  status: TransactionStatus;
  statusSummary: TransactionStatusSummary;
  statusText: string;
  createdAt: string;
  lastUpdatedAt: string;
  source: ApiTransactionEnd;
  destination: ApiTransactionEnd;
  sourceAddress: string;
  destinationAddress: string;
  destinationAddressDescription: string;
  destinationTag: string;
  txHash: string;
  note: string | null;
  hasUsdEquivalent: boolean;
  hasFeeUsdEquivalent: boolean;
  inbound: boolean;
  otherWalletSummary: string;
  explorerUrl: string | null;
}

export type Transactions = {
  transactions: Array<ChilledTransaction>;
  next: string | null;
  previous: string | null;
};

@Injectable({
  providedIn: 'root',
})
export class ChilledVaultService {
  private readonly http = inject(HttpClient);
  private readonly imageService = inject(ImageService);
  private readonly marketService = inject(MarketService);

  // Cache the full supported assets forever
  readonly $supportedAssets: Observable<Array<ChilledAsset>> = combineLatest([
    this.http.get(URLS.chilledVaultSupportedAssets).pipe(map(R.Array(ApiChilledAsset).check)),
    this.marketService.currencies,
  ]).pipe(map(toChilledAsset), shareReplay(1));

  public getVault(): Observable<Vault | undefined> {
    return combineLatest([
      this.http.get(URLS.chilledVault).pipe(
        map(ApiVault.nullable().check), //
      ),
      this.marketService.currencies,
    ]).pipe(map(toVault));
  }

  createWallet(asset: ApiChilledAsset): Observable<void> {
    return this.http.post<void>(URLS.chilledVaultCreateWallet(asset.currencyCd, asset.network), null);
  }

  public addresses(asset: string, network: string): Observable<Array<WalletAddress>> {
    return this.http.get(URLS.chilledVaultWalletAddresses(asset, network)).pipe(
      map(R.Array(ApiWalletAddress).check),
      map((addresses) => addresses.map((address) => this.toAddress(address))),
    );
  }

  public send(wallet: ChilledWallet, network: string, amount: number, destination: SendAddress, note: string): Observable<void> {
    let address: string | null = null;
    let tag: string | null = null;

    if (destination.type === SendAddressType.CRYPTO_ADDRESS) {
      if (wallet.currency.compositeAddress) {
        const fullAddess = destination.submitAddress;
        const tagStartIndex = fullAddess.indexOf('?');
        if (tagStartIndex >= 0) {
          address = fullAddess.substring(0, tagStartIndex);
          tag = fullAddess.substring(
            fullAddess.indexOf(wallet.currency.compositeAddressStringTagName!) + wallet.currency.compositeAddressStringTagName!.length + 1,
          );
        } else {
          address = destination.submitAddress;
        }
      } else {
        address = destination.submitAddress;
      }
    }

    return this.http.post<void>(URLS.chilledVaultRedeem, {
      currencyCd: wallet.currency.id,
      network,
      amount,
      toTrading: destination.type === SendAddressType.TRADING_WALLET,
      address: address,
      tag: tag,
      note,
    });
  }

  public getTransactions(
    wallet: ChilledWallet | null,
    inbound: boolean,
    next: string | null,
    start?: string,
    end?: string,
    limit = 200,
  ): Observable<Transactions> {
    return combineLatest([
      this.http
        .post(URLS.chilledVaultTransactions, {
          inbound,
          assets: wallet
            ? wallet?.networks.map((n) => {
                return { currencyCd: wallet?.currency.id, network: n };
              })
            : [],
          next,
          limit,
          start,
          end,
        })
        .pipe(map(ApiTransactionsResponse.check)),
      this.marketService.currencies,
    ]).pipe(map(toTransactions(inbound)));
  }

  private toAddress(address: ApiWalletAddress): WalletAddress {
    return {
      ...address,
      addressQRCode: this.imageService.makeImageUrl(address.addressQRCode),
      tagQRCode: this.imageService.makeImageUrl(address.tagQRCode),
    };
  }
}

const toVault = ([rawVault, currencies]: [ApiVault | null, Array<Currency>]): Vault | undefined =>
  rawVault
    ? {
        ...rawVault,
        assets: rawVault.assets.map((asset): ChilledWallet => {
          const currency = currencies.filter((c) => c.id === asset.currencyCd)[0];
          return {
            available: new BigNumber(asset.available),
            availableUsd: asset.availableUsd ? new BigNumber(asset.availableUsd) : null,
            currency,
            networks: asset.networks,
            walletExplorerUrls: currency.walletExplorerUrls,
            txnExplorerUrls: currency.txnExplorerUrls,
          };
        }),
      }
    : undefined;

const toChilledAsset = ([assets, currencies]: [Array<ApiChilledAsset>, Array<Currency>]) =>
  assets.map((asset) => ({ ...asset, currency: currencies.filter((c) => c.id === asset.currencyCd)[0] }));

const toTransactions =
  (inbound: boolean) =>
  ([apiTransactions, currencies]: [ApiTransactionsResponse, Array<Currency>]): Transactions => {
    return {
      transactions: apiTransactions.transactions
        .filter((t) => currencies.some((c) => c.id == t.currencyCd))
        .map((t) => toTransaction(t, currencies, inbound)),
      next: apiTransactions.next,
      previous: apiTransactions.previous,
    };
  };

const toTransaction = (apiTrans: ApiTransaction, currencies: Array<Currency>, inbound: boolean): ChilledTransaction => {
  const currency = currencies.find((c) => c.id === apiTrans.currencyCd)!;

  let otherWalletSummary: string;
  if (inbound) {
    otherWalletSummary = apiTrans.sourceAddress;
  } else if (!inbound && !(currency?.compositeAddress ?? false)) {
    otherWalletSummary = apiTrans.destinationAddress;
  } else {
    otherWalletSummary = `${apiTrans.destinationAddress}?${currency.compositeAddressStringTagName}=${apiTrans.destinationTag}`;
  }

  return {
    id: apiTrans.txHash,
    currency,
    network: apiTrans.network,
    feeCurrency: currencies.find((c) => c.id === apiTrans.feeCurrencyCd)!,
    amount: apiTrans.amount,
    amountUsd: apiTrans.amountUsd,
    fee: apiTrans.fee,
    feeUsd: apiTrans.feeUsd,
    status: apiTrans.status,
    statusSummary: toStatusSummary(apiTrans.status),
    statusText: toStatusText(apiTrans.status),
    createdAt: apiTrans.createdAt,
    lastUpdatedAt: apiTrans.lastUpdatedAt,
    source: apiTrans.source,
    destination: apiTrans.destination,
    sourceAddress: apiTrans.sourceAddress,
    destinationAddress: apiTrans.destinationAddress,
    destinationAddressDescription: apiTrans.destinationAddressDescription,
    destinationTag: apiTrans.destinationTag,
    txHash: apiTrans.txHash,
    note: apiTrans.note,
    hasUsdEquivalent: defined(apiTrans.amountUsd),
    hasFeeUsdEquivalent: defined(apiTrans.feeUsd),
    inbound,
    otherWalletSummary,
    explorerUrl:
      apiTrans.txHash && apiTrans.network && currency.txnExplorerUrls.has(apiTrans.network)
        ? currency.txnExplorerUrls.get(apiTrans.network)!.replace('{txnHash}', apiTrans.txHash)
        : null,
  };
};

const toStatusSummary = (status: TransactionStatus): TransactionStatusSummary => {
  switch (status) {
    case TransactionStatus.SUBMITTED:
    case TransactionStatus.QUEUED:
    case TransactionStatus.PENDING_AUTHORIZATION:
    case TransactionStatus.PENDING_SIGNATURE:
    case TransactionStatus.BROADCASTING:
    case TransactionStatus.PENDING_3RD_PARTY_MANUAL_APPROVAL:
    case TransactionStatus.PENDING_3RD_PARTY:
    case TransactionStatus.CONFIRMING:
    case TransactionStatus.PARTIALLY_COMPLETED:
    case TransactionStatus.PENDING_AML_SCREENING:
      return TransactionStatusSummary.IN_PROGRESS;
    case TransactionStatus.COMPLETED:
      return TransactionStatusSummary.SUCCEEDED;
    case TransactionStatus.CANCELLED:
    case TransactionStatus.REJECTED:
    case TransactionStatus.BLOCKED:
    case TransactionStatus.FAILED:
      return TransactionStatusSummary.FAILED;
    case TransactionStatus.UNKNOWN:
      return TransactionStatusSummary.UNKNOWN;
    default:
      return TransactionStatusSummary.UNKNOWN;
  }
};

const toStatusText = (status: TransactionStatus): string => {
  switch (status) {
    case TransactionStatus.SUBMITTED:
      return 'Submitted';
    case TransactionStatus.QUEUED:
      return 'Queued';
    case TransactionStatus.PENDING_AUTHORIZATION:
      return 'Pending Authorization';
    case TransactionStatus.PENDING_SIGNATURE:
      return 'Pending Signature';
    case TransactionStatus.BROADCASTING:
      return 'Broadcasting';
    case TransactionStatus.PENDING_3RD_PARTY_MANUAL_APPROVAL:
      return 'Pending 3rd Party Manual Approval';
    case TransactionStatus.PENDING_3RD_PARTY:
      return 'Pending 3rd Party';
    case TransactionStatus.CONFIRMING:
      return 'Confirming';
    case TransactionStatus.PARTIALLY_COMPLETED:
      return 'Partially Completed';
    case TransactionStatus.PENDING_AML_SCREENING:
      return 'Pending AML Screening';
    case TransactionStatus.COMPLETED:
      return 'Completed';
    case TransactionStatus.CANCELLED:
      return 'Cancelled';
    case TransactionStatus.REJECTED:
      return 'Rejected';
    case TransactionStatus.BLOCKED:
      return 'Blocked';
    case TransactionStatus.FAILED:
      return 'Failed';
    case TransactionStatus.UNKNOWN:
      return 'Unknown';
    default:
      return '';
  }
};
